import { useTaskModal } from "@/features/tasks/TaskModal";
import { validateTask } from "@/features/tasks/taskUtils";
import { useNotification } from "@/common/notifications";
import { useEventResolutionEnabled } from "../hooks/useEventResolutionEnabled";
import { StatusLabel } from "@/features/tasks/TaskComponents";
import moment from "moment";
import { EventConnectorStatus } from "./EventConnectorStatus";
import { EventListContent } from "./EventListContent";
import { EventConnectorDialog } from "./EventConnectorDialog";
import { EventMetaDiff } from "./EventMetaDiff";
import { eventErrorFileUrl, eventFileUrl, eventReprocessLink } from "@/features/events/utils/eventDetails";
import { projectService, tasksService } from "@/app/providers/services";
import { JsonEditor } from "@/common/JsonEditor";
import { type EventConnector, findConnectors } from "@/pages/admin/Settings/settings.api";
import { setScope, useScope } from "@/redux/scope";
import Button from "@imminently/decisively-ui/Button";
import Chip from "@imminently/decisively-ui/Chip";
import Tabs from "@imminently/decisively-ui/Tabs";
import DetailGrid from "@imminently/decisively-ui/DetailGrid";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { type Event, getEventFactUrl, getEventFactLabel } from "@packages/commons";
import { ArrowUpRight, FileText, FileWarning, ClipboardList, RotateCcw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";

interface EventDetailsProps {
  event: Event;
  compact?: boolean;
  showOpenProject?: boolean;
  connectors?: EventConnector[];
  onSetConnector?: (eventType: string) => void;
}

const objectValue = (value: unknown): Record<string, any> => {
  if (typeof value === "string") {
    try {
      return objectValue(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" ? (value as Record<string, any>) : {};
};

export const EventDetails = ({ event, connectors, onSetConnector, compact = false, showOpenProject = true }: EventDetailsProps) => {
  const resolutionEnabled = useEventResolutionEnabled();
  const hasResolutionTask = resolutionEnabled && Boolean(event.resolution?.taskId);
  const openTaskModal = useTaskModal();
  const queryClient = useQueryClient();
  const notify = useNotification();
  const [openingTask, setOpeningTask] = useState(false);
  const openTask = async () => {
    if (!event.resolution?.taskId) return;
    setOpeningTask(true);
    try {
      const task = await tasksService.getOne(event.resolution.taskId);
      if (!task?.id) throw new Error("Task not found");
      openTaskModal(validateTask(task), () => {
        void queryClient.invalidateQueries({ predicate: query => query.queryKey.includes("events") });
      });
    } catch (error) {
      notify.error(error instanceof Error ? error.message : "Unable to open task");
    } finally {
      setOpeningTask(false);
    }
  };
  const [connectorEvent, setConnectorEvent] = useState<string>();
  const dispatch = useDispatch();
  const scope = useScope();
  const history = useHistory();
  const [openingProject, setOpeningProject] = useState(false);
  const [projectError, setProjectError] = useState("");
  const openProject = async (projectId = event.modelId) => {
    if (!projectId) return;
    setOpeningProject(true);
    setProjectError("");
    try {
      const project = await projectService.getOne(projectId);
      dispatch(setScope({ ...scope, workspace: project.workspace, project: project.id, release: null }));
      history.push("/build");
    } catch {
      setProjectError("Unable to open project");
    } finally {
      setOpeningProject(false);
    }
  };
  const tenancyId = localStorage.getItem("decisively.tenant");
  const connectorQuery = useQuery(
    ["event-detail-connectors", tenancyId],
    async () => {
      if (!tenancyId) return [];
      const response = await findConnectors.submit({ params: { id: tenancyId } });
      const data = response?.data ?? response;
      return (Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []) as EventConnector[];
    },
    { enabled: connectors === undefined && Boolean(tenancyId) },
  );
  const meta = objectValue(event.meta);
  const identity = objectValue(event.identity);
  const roles = Array.isArray(identity.roles) ? identity.roles : [];
  const statusVariant =
    event.status === "success"
      ? "success"
      : event.status === "error"
        ? "destructive"
        : event.status === "warning"
          ? "warning"
          : "secondary";
  const errorFileUrl = eventErrorFileUrl(event);
  const fileUrl = eventFileUrl(event);
  const reprocessLink = eventReprocessLink(event);
  const facts = event.facts || [];
  const fields: [string, ReactNode][] = [
    ["Event type", event.eventType],
    ["Resolution", hasResolutionTask && event.resolution?.status ? <StatusLabel status={event.resolution.status} /> : undefined],
    ["Last reviewed", hasResolutionTask && event.resolution?.updatedAt ? new Date(event.resolution.updatedAt).toLocaleString() : undefined],
    ["Reviewed by", hasResolutionTask ? event.resolution?.updatedBy : undefined],
    ["Recorded", event.timestamp ? new Date(event.timestamp).toLocaleString() : undefined],
    ["Project ID", event.modelId],
    ["Session ID", event.sessionId],
    ["Release ID", event.releaseId],
    ["Request ID", event.requestId],
  ];
  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden bg-white ${compact ? "text-xs" : ""}`}>
      {connectorEvent && <EventConnectorDialog eventType={connectorEvent} onClose={() => setConnectorEvent(undefined)} />}
      <header className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <EventListContent expanded eventType={event.eventType} message={event.message}
              timestamp={event.timestamp ? moment(event.timestamp).format("YYYY-MM-DD HH:mm:ss") : "N/A"} />
          </div>
          <Chip size="sm" variant={statusVariant} label={event.status || "Unknown"} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {fileUrl && (
            <Button size="sm" variant="ghost" label="Open File" onClick={() => history.push(fileUrl)}>
              <FileText size={14} /> Open File
            </Button>
          )}
          {errorFileUrl && (
            <Button size="sm" variant="ghost" label="Open Error File" onClick={() => history.push(errorFileUrl)}>
              <FileWarning size={14} /> Open Error File
            </Button>
          )}
          {reprocessLink && (
            <Button size="sm" variant="ghost" label={reprocessLink.label} onClick={() => history.push(reprocessLink.url)}>
              <RotateCcw size={14} /> {reprocessLink.label}
            </Button>
          )}
          {showOpenProject && event.modelId && (
            <Button size="sm" variant="ghost" label="Open project" disabled={openingProject} onClick={() => openProject()}>
              <ArrowUpRight size={14} /> Open project
            </Button>
          )}
          {event.sessionId && (
            <Button size="sm" variant="ghost" label="Open session" onClick={() => history.push(`/monitor/overview/${encodeURIComponent(event.sessionId!)}`)}>
              <ArrowUpRight size={14} /> Open session
            </Button>
          )}
          <EventConnectorStatus
            eventType={event.eventType}
            connectors={connectors ?? connectorQuery.data ?? []}
            loading={connectors === undefined && connectorQuery.isLoading}
            error={connectors === undefined && connectorQuery.isError}
            onSetConnector={onSetConnector ?? setConnectorEvent}
          />
          {hasResolutionTask && (
            <Button className="ml-auto" size="sm" variant="ghost" label="Open task" disabled={openingTask} onClick={openTask}>
              <ClipboardList size={14} /> Open task
            </Button>
          )}
        </div>
        {projectError && <p role="alert" className="mt-2 text-sm text-destructive">{projectError}</p>}
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        <FormPanel className="shrink-0">
          <DetailGrid>
            {fields
              .filter(([, value]) => value !== undefined && value !== null && value !== "")
              .map(([label, value]) => (
                <DetailGrid.Item key={label}>
                  <DetailGrid.Title>{label}</DetailGrid.Title>
                  <DetailGrid.Content className="min-w-0 break-all">{value}</DetailGrid.Content>
                </DetailGrid.Item>
              ))}
          </DetailGrid>
        </FormPanel>
        {Array.isArray(facts) && facts.length > 0 && (
          <FormPanel className="mt-4 shrink-0">
            <FormPanel.Title>Facts</FormPanel.Title>
            <DetailGrid>
              {facts.map((fact, index) => {
                const url = getEventFactUrl(fact.url);
                return (
                  <DetailGrid.Item key={index}>
                    <DetailGrid.Title>{getEventFactLabel(fact, event.eventType)}</DetailGrid.Title>
                    <DetailGrid.Content className="min-w-0 break-all">
                      {url ? (
                        url.startsWith("/") ? (
                          <Link className="inline-flex items-center gap-1 text-primary underline underline-offset-2" to={url}>
                            {String(fact.value ?? fact.url)} <ArrowUpRight size={14} className="shrink-0" />
                          </Link>
                        ) : (
                          <a className="inline-flex items-center gap-1 text-primary underline underline-offset-2" href={url} target="_blank" rel="noopener noreferrer">
                            {String(fact.value ?? fact.url)} <ArrowUpRight size={14} className="shrink-0" />
                          </a>
                        )
                      ) : String(fact.value ?? "")}
                    </DetailGrid.Content>
                  </DetailGrid.Item>
                );
              })}
            </DetailGrid>
          </FormPanel>
        )}
        {(identity.full_name || identity.email || identity.clientIP || roles.length > 0) && (
          <FormPanel className="mt-4 shrink-0">
            <FormPanel.Title>Identity</FormPanel.Title>
            <DetailGrid>
              {[
                ["Name", identity.full_name],
                ["Email", identity.email],
                ["Client IP", identity.clientIP],
                ["Roles", roles.join(", ")],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <DetailGrid.Item key={label}>
                    <DetailGrid.Title>{label}</DetailGrid.Title>
                    <DetailGrid.Content className="break-all">{value}</DetailGrid.Content>
                  </DetailGrid.Item>
                ))}
            </DetailGrid>
          </FormPanel>
        )}
        <section className="mt-4 flex min-h-[28rem] flex-1 flex-col gap-3 border-t border-border pt-4">
          <FormPanel.Title>Event data</FormPanel.Title>
          <Tabs defaultValue="meta" className="flex min-h-0 flex-1 flex-col">
            <Tabs.List>
              <Tabs.Trigger value="meta">Meta</Tabs.Trigger>
              <Tabs.Trigger value="raw">Raw</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="meta" className="min-h-0 flex-1 overflow-hidden">
              {event.metaFormat === "diff" ? (
                <EventMetaDiff meta={meta} />
              ) : (
                <JsonEditor value={JSON.stringify(meta, null, 2)} readOnly withCopy />
              )}
            </Tabs.Content>
            <Tabs.Content value="raw" className="min-h-0 flex-1 overflow-hidden">
              <JsonEditor value={JSON.stringify(event, null, 2)} readOnly withCopy />
            </Tabs.Content>
          </Tabs>
        </section>
      </div>
    </div>
  );
};
